import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import Header from "./components/Header";
import Player from "./components/Player";
import MiniGames from "./components/MiniGames";
import Chat from "./components/Chat";
import Footer from "./components/Footer";
import Schedule from "./components/Schedule";
import Podcasts from "./components/Podcasts";
import AboutUs from "./components/AboutUs";

import "./styles.css";

// Home page: intro text, the player and the live chat.
function Home({ audioRef, isPlaying, togglePlay, volume, setVolume, muted, setMuted }) {
  return (
    <div className="container">
      <section className="hero text-center py-16" aria-hidden="true" />

      <p className="joker-text text-gray-300 text-center">
        Αν ψάχνετε για επαγγελματίες, μάλλον κάνατε λάθος κλικ!
        Στο ΤΑΨΙ η μουσική επιλέγεται με ένστικτο και οι εκπομπές
        επιβιώνουν από καθαρή τύχη.
        <span className="kali-akroasi">Καλή Ακρόαση!</span>
      </p>

      <div className="home-live-section">
        <div className="player-section">
          <Player
            audioRef={audioRef}
            isPlaying={isPlaying}
            togglePlay={togglePlay}
            volume={volume}
            setVolume={setVolume}
            muted={muted}
            setMuted={setMuted}
          />
        </div>

        <div className="chat-section">
          <Chat />
        </div>
      </div>
    </div>
  );
}

const STREAM_URL = "https://a9.asurahosting.com/listen/tapsi_web_radio/radio.mp3";

export default function App() {
  // A single Audio element is shared by the whole app so playback survives route changes
  const audioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [muted, setMuted] = useState(false);

  // Create the audio element once and keep isPlaying in sync with it
  useEffect(() => {
    if (!audioRef.current) {
      const audio = new Audio(STREAM_URL);
      audio.preload = "none";
      audio.crossOrigin = "anonymous";
      audioRef.current = audio;
    }

    const audio = audioRef.current;
    const handlePlaying = () => setIsPlaying(true);
    const handleStopped = () => setIsPlaying(false);

    audio.addEventListener("playing", handlePlaying);
    audio.addEventListener("pause", handleStopped);
    audio.addEventListener("ended", handleStopped);
    audio.addEventListener("error", handleStopped);

    return () => {
      audio.removeEventListener("playing", handlePlaying);
      audio.removeEventListener("pause", handleStopped);
      audio.removeEventListener("ended", handleStopped);
      audio.removeEventListener("error", handleStopped);
      audio.pause();
      audio.src = "";
    };
  }, []);

  // Push volume / mute changes to the audio element
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      return;
    }

    // Reload so we join the live stream instead of resuming stale buffered audio
    if (!audio.src || !audio.src.includes("radio.mp3")) audio.src = STREAM_URL;
    audio.load();
    audio.play().catch((err) => {
      console.warn("Audio playback error:", err);
      setIsPlaying(false);
    });
  };

  return (
    <>
      <Header />

      {/* Unknown URLs are redirected home instead of showing a 404 */}
      <Routes>
        <Route
          path="/"
          element={
            <Home
              audioRef={audioRef}
              isPlaying={isPlaying}
              togglePlay={togglePlay}
              volume={volume}
              setVolume={setVolume}
              muted={muted}
              setMuted={setMuted}
            />
          }
        />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/podcasts" element={<Podcasts />} />
        <Route path="/mini-games" element={<MiniGames />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Footer />
    </>
  );
}